<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import { AppLoadingState, StatusBadge } from '../../framework/components/app';

  export let teamUuid: string;

  interface InvoiceRow {
    uuid: string;
    invoice_number: string;
    invoice_date: string;
    due_date: string;
    total: number;
    currency: string;
    status: string;
    is_paid: boolean;
    user?: { first_name: string; last_name: string; email: string } | null;
  }

  const moneyFmt = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

  let items: InvoiceRow[] = [];
  let isLoading = false;
  let currentPage = 1;
  let paging = { last_page: 1, total: 0, from: 0, to: 0 };

  onMount(() => loadInvoices());

  async function loadInvoices() {
    if (!teamUuid) return;
    isLoading = true;
    try {
      const response: any = await api.getAdminTeamInvoices(teamUuid, {
        page: currentPage,
        per_page: 10,
      });
      if (response.success) {
        items = response.data || [];
        paging = response.paging || paging;
      } else {
        items = [];
        toast.showNotification(false, response.message || 'Failed to load invoices');
      }
    } catch (error) {
      items = [];
      console.error(error);
    } finally {
      isLoading = false;
    }
  }

  function formatDate(value: string): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
  }

  function formatMoney(row: InvoiceRow): string {
    try {
      return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency: (row.currency || 'USD').toUpperCase(),
        minimumFractionDigits: 2,
      }).format(row.total);
    } catch {
      return moneyFmt.format(row.total);
    }
  }

  function statusTone(status: string, isPaid: boolean): 'success' | 'warn' | 'danger' | 'neutral' | 'info' {
    if (isPaid || status === 'paid') return 'success';
    if (status === 'overdue') return 'danger';
    if (status === 'sent') return 'info';
    if (status === 'draft') return 'neutral';
    return 'warn';
  }

  async function downloadInvoice(uuid: string, event: Event) {
    event.stopPropagation();
    try {
      await api.downloadInvoicePdf(uuid);
    } catch (error) {
      toast.showNotification(false, 'Failed to download invoice');
      console.error(error);
    }
  }

  function goToPage(page: number) {
    currentPage = page;
    loadInvoices();
  }
</script>

<div class="admin-list admin-list--nested">
{#if isLoading}
  <AppLoadingState label="Loading invoices…" />
{:else if items.length === 0}
  <div class="empty-section">No invoices found for this team.</div>
{:else}
  <div class="table-wrap">
    <table class="data-table">
      <thead>
        <tr>
          <th><i class="hashtag icon" aria-hidden="true"></i> Invoice</th>
          <th><i class="calendar icon" aria-hidden="true"></i> Date</th>
          <th><i class="calendar check icon" aria-hidden="true"></i> Due</th>
          <th class="num-col"><i class="dollar sign icon" aria-hidden="true"></i> Amount</th>
          <th><i class="signal icon" aria-hidden="true"></i> Status</th>
          <th><i class="user icon" aria-hidden="true"></i> Billed to</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {#each items as row (row.uuid)}
          <tr>
            <td class="invoice-num">{row.invoice_number}</td>
            <td class="date-cell">{formatDate(row.invoice_date)}</td>
            <td class="date-cell">{formatDate(row.due_date)}</td>
            <td class="num-col">{formatMoney(row)}</td>
            <td>
              <StatusBadge tone={statusTone(row.status, row.is_paid)}>
                {row.is_paid ? 'Paid' : row.status}
              </StatusBadge>
            </td>
            <td class="user-cell">
              {#if row.user}
                {row.user.first_name} {row.user.last_name}
              {:else}
                —
              {/if}
            </td>
            <td class="actions-cell">
              <button type="button" class="download-btn" on:click={(e) => downloadInvoice(row.uuid, e)} aria-label="Download invoice {row.invoice_number}">
                <i class="download icon" aria-hidden="true"></i>
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  {#if paging.last_page > 1}
    <div class="pagination">
      <span class="pagination-info">Showing {paging.from || 0}–{paging.to || 0} of {paging.total}</span>
      <div class="pagination-buttons">
        <button type="button" class="pagination-btn" disabled={currentPage <= 1} on:click={() => goToPage(currentPage - 1)} aria-label="Previous page">Previous</button>
        <span class="page-indicator">Page {currentPage} of {paging.last_page}</span>
        <button type="button" class="pagination-btn" disabled={currentPage >= paging.last_page} on:click={() => goToPage(currentPage + 1)} aria-label="Next page">Next</button>
      </div>
    </div>
  {/if}
  {/if}
</div>

<style>
  .invoice-num { font-weight: 500; font-variant-numeric: tabular-nums; }

  .download-btn {
    border: none;
    background: transparent;
    color: var(--primary);
    cursor: pointer;
    padding: var(--space-2);
    border-radius: var(--radius-input);
  }

  .download-btn:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
</style>
